import type { StructureDef, ZoneDef } from './types.js';

const HALF_PI = Math.PI / 2;

/** Straight fence run from (x0,z0) to (x1,z1), split into ~6 m panels, leaving `gaps` (along-run intervals) open. */
function fenceRun(
  x0: number,
  z0: number,
  x1: number,
  z1: number,
  gaps: [number, number][] = [],
): StructureDef[] {
  const dx = x1 - x0;
  const dz = z1 - z0;
  const len = Math.sqrt(dx * dx + dz * dz);
  const ux = dx / len;
  const uz = dz / len;
  // A box's local +X runs along the fence: worldX = cos(rot), worldZ = -sin(rot)  =>  rot = atan2(-uz, ux)
  const rot = Math.atan2(-uz, ux);
  const out: StructureDef[] = [];
  const panel = 6;
  // Build open intervals along the run, removing the gaps.
  const solid: [number, number][] = [];
  let cursor = 0;
  for (const [g0, g1] of [...gaps].sort((a, b) => a[0] - b[0])) {
    if (g0 > cursor) solid.push([cursor, g0]);
    cursor = Math.max(cursor, g1);
  }
  if (cursor < len) solid.push([cursor, len]);
  for (const [s0, s1] of solid) {
    const n = Math.max(1, Math.round((s1 - s0) / panel));
    const step = (s1 - s0) / n;
    for (let i = 0; i < n; i++) {
      const mid = s0 + step * (i + 0.5);
      out.push({ kind: 'fence', x: x0 + ux * mid, z: z0 + uz * mid, rot, w: step, d: 0.3, h: 1.3 });
    }
  }
  return out;
}

/** Ring of palisade logs around (cx,cz) leaving an opening centred on `openingYaw`. */
function palisadeRing(
  cx: number,
  cz: number,
  r: number,
  segments: number,
  openingYaw: number,
  openingHalf: number,
): StructureDef[] {
  const out: StructureDef[] = [];
  const segLen = (2 * Math.PI * r) / segments;
  for (let i = 0; i < segments; i++) {
    const a = (i + 0.5) * ((2 * Math.PI) / segments);
    let diff = Math.abs(
      ((((a - openingYaw + Math.PI) % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI)) - Math.PI,
    );
    if (diff < openingHalf) continue;
    // Point on circle in yaw convention: (sin a, cos a).
    const x = cx + Math.sin(a) * r;
    const z = cz + Math.cos(a) * r;
    // Tangent direction (cos a, -sin a) => box rot = atan2(sin a, cos a) = a.
    out.push({ kind: 'palisade', x, z, rot: a, w: segLen + 0.2, d: 0.6, h: 3.2 });
  }
  return out;
}

const CAMP = { x: 76, z: 38 };
const campOpeningYaw = Math.atan2(62 - CAMP.x, 30 - CAMP.z);

export const BEGINNER_VILLAGE: ZoneDef = {
  id: 'willow_creek',
  name: 'Willow Creek Village',
  nameZh: '柳溪村',
  bounds: { minX: -110, maxX: 110, minZ: -110, maxZ: 110 },
  seed: 20261019,
  spawn: { x: 0, z: 15, fx: 0, fz: -1 },
  respawn: { x: 0, z: 15 },
  safeZones: [{ x: 0, z: 4, r: 40 }],
  plaza: { x: 0, z: 6, r: 10 },
  structures: [
    // --- Village core
    { kind: 'well', x: 0, z: 6, rot: 0, w: 2.4, d: 2.4 },
    {
      kind: 'hall',
      x: -14,
      z: -17,
      rot: 0,
      w: 13,
      d: 8,
      h: 5.6,
      wallColor: '#ece2cf',
      roofColor: '#2e3d4f',
      label: 'Elder’s Hall',
    },
    { kind: 'stone_lantern', x: -19.5, z: -9.5, rot: 0, w: 0.8, d: 0.8 },
    { kind: 'stone_lantern', x: -8.5, z: -9.5, rot: 0, w: 0.8, d: 0.8 },
    { kind: 'house', x: 14, z: -15, rot: 0, w: 9, d: 7, h: 4.2, wallColor: '#e6dcc6', roofColor: '#3b3b44' },
    {
      kind: 'house',
      x: -24,
      z: 6,
      rot: HALF_PI,
      w: 9,
      d: 7,
      h: 4.2,
      wallColor: '#e9e0cc',
      roofColor: '#4a3a36',
    },
    {
      kind: 'house',
      x: 21,
      z: -4,
      rot: 0,
      w: 9,
      d: 7,
      h: 4.4,
      wallColor: '#e3e8d8',
      roofColor: '#2f4a3a',
      label: 'Lin’s Apothecary',
    },
    {
      kind: 'house',
      x: -17,
      z: 28,
      rot: Math.PI,
      w: 10,
      d: 7,
      h: 4.2,
      wallColor: '#e8dccb',
      roofColor: '#3b3b44',
    },
    {
      kind: 'house',
      x: 17,
      z: 28,
      rot: Math.PI,
      w: 10,
      d: 7,
      h: 4.6,
      wallColor: '#d9cdb8',
      roofColor: '#3a2f2a',
      label: 'Zhou’s Forge',
    },
    { kind: 'anvil', x: 12.5, z: 22.5, rot: 0.2, w: 1.2, d: 0.6 },
    { kind: 'crate_stack', x: 21.5, z: 22.8, rot: 0.1, w: 1.6, d: 1.6 },
    { kind: 'stall', x: 8.5, z: -2.5, rot: -0.65, w: 3.2, d: 2, roofColor: '#9a3b2e' },
    { kind: 'stall', x: -9.5, z: 14, rot: Math.PI + 0.8, w: 3.2, d: 2, roofColor: '#c9a35a' },
    { kind: 'stall', x: 9.5, z: 14, rot: Math.PI - 0.8, w: 3.2, d: 2, roofColor: '#2f6f6a' },
    { kind: 'pond', x: -24, z: -27, rot: 0, w: 10, d: 10 },
    { kind: 'watchtower', x: 29.5, z: -2, rot: 0, w: 3, d: 3, h: 9 },
    // --- Gates (paifang archways)
    { kind: 'gate', x: 34, z: 6, rot: HALF_PI, w: 6.4, d: 1, h: 6, label: 'Eastern Gate' },
    { kind: 'gate', x: 0, z: -37, rot: 0, w: 6.4, d: 1, h: 6, label: 'Northern Gate' },
    { kind: 'gate', x: 0, z: 44, rot: 0, w: 6.4, d: 1, h: 6, label: 'Southern Gate' },
    // --- Village fence (rectangle with road openings)
    ...fenceRun(-34, -37, 34, -37, [[30.6, 37.4]]),
    ...fenceRun(-34, 44, 34, 44, [[30.6, 37.4]]),
    ...fenceRun(34, -37, 34, 44, [[39.6, 46.4]]),
    ...fenceRun(-34, -37, -34, 44),
    // --- Stream crossing
    { kind: 'bridge', x: 0, z: -46.5, rot: 0, w: 7, d: 11 },
    // --- Waypoint shrines
    { kind: 'shrine', x: -5, z: 18.5, rot: 0.5, w: 1.6, d: 1.0, label: 'Village Square Shrine' },
    { kind: 'shrine', x: 9, z: -63, rot: -0.4, w: 1.6, d: 1.0, label: 'Northern Meadow Shrine' },
    { kind: 'shrine', x: 55.5, z: 16.5, rot: Math.PI - 0.9, w: 1.6, d: 1.0, label: 'Eastern Road Shrine' },
    // --- Bandit camp
    ...palisadeRing(CAMP.x, CAMP.z, 14, 22, campOpeningYaw, 0.42),
    { kind: 'tent', x: CAMP.x + 5, z: CAMP.z - 6, rot: 0.4, w: 4.2, d: 4.2, roofColor: '#7b6a4e' },
    { kind: 'tent', x: CAMP.x + 6, z: CAMP.z + 5, rot: -0.3, w: 4.6, d: 4.6, roofColor: '#6e4f3a' },
    { kind: 'tent', x: CAMP.x - 3, z: CAMP.z + 8, rot: 1.2, w: 3.8, d: 3.8, roofColor: '#7b6a4e' },
    { kind: 'campfire', x: CAMP.x, z: CAMP.z, rot: 0, w: 1.8, d: 1.8 },
    { kind: 'weapon_rack', x: CAMP.x + 9.5, z: CAMP.z, rot: -HALF_PI, w: 2.4, d: 0.6 },
    { kind: 'crate_stack', x: CAMP.x - 6, z: CAMP.z - 7, rot: 0.3, w: 1.8, d: 1.8 },
    { kind: 'watchtower', x: CAMP.x + 15, z: CAMP.z - 16, rot: 0.2, w: 3, d: 3, h: 8 },
    // --- Fields south
    { kind: 'haystack', x: 18, z: 58, rot: 0, w: 2.4, d: 2.4 },
    { kind: 'haystack', x: -22, z: 66, rot: 0, w: 2.4, d: 2.4 },
    { kind: 'haystack', x: 34, z: 80, rot: 0, w: 2.4, d: 2.4 },
    // --- Rocks of note
    { kind: 'rock', x: -12, z: -70, rot: 0.3, w: 3.2, d: 3.2 },
    { kind: 'rock', x: 16, z: -88, rot: 1.1, w: 2.6, d: 2.6 },
    { kind: 'rock', x: 30, z: -66, rot: 2.1, w: 3.6, d: 3.6 },
    { kind: 'rock', x: -34, z: -86, rot: 0.8, w: 2.8, d: 2.8 },
    { kind: 'rock', x: 48, z: 26, rot: 0.5, w: 2.4, d: 2.4 },
  ],
  water: [
    // Stream: blocked everywhere except across the bridge deck (|x| <= 3.6).
    { x: -56.8, z: -46.5, hw: 53.2, hd: 3.5, rot: 0 },
    { x: 56.8, z: -46.5, hw: 53.2, hd: 3.5, rot: 0 },
  ],
  roads: [
    {
      kind: 'stone',
      width: 4,
      points: [
        [0, -3],
        [0, -37],
        [0, -52],
        [3, -58],
        [8, -61],
      ],
    },
    {
      kind: 'dirt',
      width: 4,
      points: [
        [9, 6],
        [34, 6],
        [50, 8],
        [58, 14],
        [62, 24],
        [64, 31],
      ],
    },
    {
      kind: 'dirt',
      width: 4,
      points: [
        [0, 15],
        [0, 44],
        [0, 80],
        [0, 110],
      ],
    },
    {
      kind: 'stone',
      width: 3,
      points: [
        [-6, -2],
        [-14, -10],
      ],
    },
    {
      kind: 'dirt',
      width: 3,
      points: [
        [-9, 6],
        [-19, 6],
      ],
    },
  ],
  fields: [
    { minX: 10, maxX: 46, minZ: 52, maxZ: 92 },
    { minX: -48, maxX: -10, minZ: 54, maxZ: 92 },
  ],
  areas: [
    { id: 'willow_creek_village', name: 'Willow Creek Village', x: 0, z: 4, r: 38 },
    { id: 'northern_meadow', name: 'Northern Meadow', x: 0, z: -76, r: 24 },
    { id: 'eastern_road', name: 'Eastern Road', x: 52, z: 10, r: 14 },
    { id: 'bandit_camp', name: 'Black Ash Bandit Camp', x: CAMP.x, z: CAMP.z, r: 18 },
    { id: 'southern_fields', name: 'Southern Rice Fields', x: 0, z: 72, r: 30 },
  ],
  npcs: [
    { npcId: 'elder_bai', x: -14, z: -11, yaw: 0.15 },
    { npcId: 'guard_captain_wen', x: 30.5, z: 11, yaw: -HALF_PI - 0.3 },
    { npcId: 'herbalist_lin', x: 21, z: 1.6, yaw: 0 },
    { npcId: 'smith_zhou', x: 16, z: 22.5, yaw: Math.PI + 0.3 },
    { npcId: 'villager_mei', x: -4, z: -1, yaw: 0.6 },
    { npcId: 'villager_ah_fu', x: 5, z: 30, yaw: -0.4 },
  ],
  monsterSpawns: [
    { id: 'wolves_west', monsterId: 'grey_wolf', x: -22, z: -75, radius: 10, count: 3 },
    { id: 'wolves_east', monsterId: 'grey_wolf', x: 22, z: -80, radius: 10, count: 3 },
    { id: 'wolves_north', monsterId: 'grey_wolf', x: -2, z: -92, radius: 7, count: 2 },
    { id: 'bandit_camp', monsterId: 'mountain_bandit', x: CAMP.x, z: CAMP.z, radius: 8, count: 5 },
  ],
  waypoints: [
    { id: 'wp_village', name: 'Village Square', x: -5, z: 18.5 },
    { id: 'wp_meadow', name: 'Northern Meadow', x: 9, z: -63 },
    { id: 'wp_east_road', name: 'Eastern Road', x: 55.5, z: 16.5 },
  ],
  scatter: [
    {
      kind: 'willow',
      region: { minX: -104, maxX: -8, minZ: -42, maxZ: -38 },
      count: 14,
      minScale: 0.9,
      maxScale: 1.3,
      spacing: 5,
    },
    {
      kind: 'willow',
      region: { minX: 8, maxX: 104, minZ: -42, maxZ: -38 },
      count: 14,
      minScale: 0.9,
      maxScale: 1.3,
      spacing: 5,
    },
    {
      kind: 'willow',
      region: { minX: -104, maxX: 104, minZ: -55, maxZ: -51 },
      count: 16,
      minScale: 0.8,
      maxScale: 1.2,
      spacing: 6,
    },
    {
      kind: 'pine',
      region: { minX: -108, maxX: -38, minZ: -36, maxZ: 104 },
      count: 90,
      minScale: 0.85,
      maxScale: 1.45,
      spacing: 4.5,
    },
    {
      kind: 'maple',
      region: { minX: -108, maxX: -38, minZ: -36, maxZ: 104 },
      count: 30,
      minScale: 0.9,
      maxScale: 1.3,
      spacing: 5,
    },
    { kind: 'pine', region: { x: 0, z: -78, r: 34 }, count: 16, minScale: 0.9, maxScale: 1.4, spacing: 7 },
    {
      kind: 'bamboo',
      region: { minX: -108, maxX: 108, minZ: -108, maxZ: -97 },
      count: 70,
      minScale: 0.9,
      maxScale: 1.3,
      spacing: 3.2,
    },
    {
      kind: 'pine',
      region: { minX: 36, maxX: 108, minZ: -95, maxZ: -56 },
      count: 30,
      minScale: 0.9,
      maxScale: 1.4,
      spacing: 5,
    },
    {
      kind: 'pine',
      region: { minX: -108, maxX: -36, minZ: -95, maxZ: -56 },
      count: 30,
      minScale: 0.9,
      maxScale: 1.4,
      spacing: 5,
    },
    {
      kind: 'pine',
      region: { minX: 38, maxX: 108, minZ: -36, maxZ: 0 },
      count: 34,
      minScale: 0.9,
      maxScale: 1.4,
      spacing: 5,
    },
    {
      kind: 'maple',
      region: { minX: 94, maxX: 108, minZ: 0, maxZ: 104 },
      count: 14,
      minScale: 0.9,
      maxScale: 1.3,
      spacing: 5,
    },
    {
      kind: 'maple',
      region: { minX: -108, maxX: 108, minZ: 50, maxZ: 106 },
      count: 36,
      minScale: 0.9,
      maxScale: 1.3,
      spacing: 6,
    },
    {
      kind: 'pine',
      region: { minX: -108, maxX: 108, minZ: 94, maxZ: 108 },
      count: 30,
      minScale: 0.9,
      maxScale: 1.4,
      spacing: 5,
    },
    {
      kind: 'bamboo',
      region: { minX: 38, maxX: 92, minZ: 54, maxZ: 92 },
      count: 22,
      minScale: 0.9,
      maxScale: 1.2,
      spacing: 4,
    },
  ],
  trees: [
    { kind: 'willow', x: -19, z: -22, scale: 1.2 },
    { kind: 'willow', x: -30, z: -20, scale: 1.1 },
    { kind: 'willow', x: -30, z: -33, scale: 1.0 },
    { kind: 'maple', x: -27, z: 16, scale: 1.0 },
    { kind: 'maple', x: 26, z: -6, scale: 1.0 },
    { kind: 'maple', x: -6, z: 34, scale: 0.9 },
    { kind: 'pine', x: 28, z: 36, scale: 1.1 },
    { kind: 'pine', x: -28, z: 38, scale: 1.0 },
    { kind: 'maple', x: 24, z: -28, scale: 1.1 },
    { kind: 'pine', x: -6, z: -28, scale: 1.0 },
  ],
  ambience: { music: 'village', fogColor: '#c9d6d8', skyTop: '#6f9fc8', skyHorizon: '#f1e3c6' },
};
